import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  AuditReadinessTestRecord,
  GetAuditReadinessRequest,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useInAudit } from 'src/context/AuditContext'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { useRefetchCallback } from 'src/pages/Roadmap_v2/smartchecks/hooks/useRefetchCallback'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'

// module-level so these keep a stable reference across renders
export const ALL_CONTROL_CHECKS = [
  CONTROL_TEST_NAMES.POLICY_MATCH,
  CONTROL_TEST_NAMES.COMPLETENESS,
  CONTROL_TEST_NAMES.SPOT_CHECK,
]

// grids whose row config hides the CPMD column only ever read the spot check
export const SPOT_CHECK_ONLY = [CONTROL_TEST_NAMES.SPOT_CHECK]

type ControlChecksContextValue = {
  getTests: (
    controlId: string,
    migratedControlId?: string,
  ) => AuditReadinessTestRecord[] | undefined
  isLoading: boolean
  isRefreshing: boolean
}

const ControlChecksContext = createContext<ControlChecksContextValue | null>(
  null,
)

/**
 * Fetches audit readiness once for the whole account so each control row can read
 * its own tests out of the result instead of fetching them itself.
 *
 * Pages that already fetch readiness should make this provider reuse that fetch.
 */
export const ControlChecksProvider = ({
  request,
  tests = ALL_CONTROL_CHECKS,
  children,
}: {
  request?: GetAuditReadinessRequest
  tests?: CONTROL_TEST_NAMES[]
  children: React.ReactNode
}): JSX.Element => {
  const { auditId } = useInAudit()
  const canViewAuditScans = useCanPerformAction(CAPABILITY.CAN_VIEW_AUDIT_SCANS)

  const readinessRequest = useMemo(
    () =>
      request ??
      new GetAuditReadinessRequest()
        .setAuditId(auditId || '')
        .setCalculateStaleness(true)
        .setControlTestsList(tests),
    [request, auditId, tests],
  )

  const { response } = useSwrImmutableGrpc(
    AuditBotPromiseClient.prototype.getAuditReadiness,
    readinessRequest,
    canViewAuditScans,
  )
  const { data, isLoading, mutate } = response
  // a count, not a bool: the poll can fire refresh again before the last fetch lands, and a
  // bool would clear on the first one to settle. SWR won't dedupe them — mutate() is explicit.
  const [refreshesInFlight, setRefreshesInFlight] = useState(0)

  // A scan updates readiness as each control test finishes, so refetch whenever the
  // live-run poll reports newly completed tests — same as the AuditBot checks grid.
  // Rows read isRefreshing to hold their spinner until the fresh result lands.
  const refresh = useCallback(() => {
    setRefreshesInFlight((n) => n + 1)
    mutate().finally(() => setRefreshesInFlight((n) => n - 1))
  }, [mutate])
  useRefetchCallback(refresh, 'ControlChecksProvider')
  const isRefreshing = refreshesInFlight > 0

  const value = useMemo<ControlChecksContextValue>(() => {
    const byControlId: Record<string, AuditReadinessTestRecord[]> = {}
    const byModelId: Record<string, AuditReadinessTestRecord[]> = {}
    data?.getAuditReadinessList().forEach((readiness) => {
      const readinessTests = readiness.getControlTestsList()
      byControlId[readiness.getControlId()] = readinessTests
      byModelId[readiness.getControlModelId()] = readinessTests
    })

    return {
      isLoading,
      isRefreshing,
      getTests: (controlId, migratedControlId) => {
        const lookup = (map: Record<string, AuditReadinessTestRecord[]>) =>
          map[controlId] ??
          (migratedControlId ? map[migratedControlId] : undefined)
        // control id first, then model id, matching the old per-row lookup order
        return lookup(byControlId) ?? lookup(byModelId)
      },
    }
  }, [data, isLoading, isRefreshing])

  return (
    <ControlChecksContext.Provider value={value}>
      {children}
    </ControlChecksContext.Provider>
  )
}

export const useControlChecksContext = (): ControlChecksContextValue => {
  const context = useContext(ControlChecksContext)
  if (!context) {
    throw new Error(
      'useControlChecksContext must be used within a ControlChecksProvider',
    )
  }
  return context
}
